import { Component, Ref, DOMAttributes, ClassAttributes, cloneElement, Children, MouseEvent, ReactElement } from 'react'
import * as Hammer from 'hammerjs'
import { generate } from 'randomstring'

type SupportedMouseHandlerNames = 'onClick' | 'onMouseEnter' | 'onMouseLeave'
type MouseHandlers = Pick<DOMAttributes<any>, SupportedMouseHandlerNames>

type TouchHandlers = {
  onTap?: HammerListener
}

// TODO make touchable generic of ref element
type TouchableProps = TouchHandlers & MouseHandlers & {
  childRef?: Ref<HTMLElement | SVGElement>
}

/**
 * It's suitable to pass these handlers to `Touchable`
 * if you want all the mouse events to be muted automatically in case of `tap` happened.
 *
 * Technically this is achieved by `preventDefault` on a native `touchend` event.
 */
const SUPPORTED_MOUSE_HANDLERS: SupportedMouseHandlerNames[] = ['onClick', 'onMouseEnter', 'onMouseLeave']

class Touchable extends Component<TouchableProps, {}> {
  static defaultProps: Partial<TouchableProps> = {
    onTap: () => { /* do nothing */ },
  }

  private rootNode: HTMLElement | null = null
  private rootHammer: HammerManager | null = null
  // this is a random string generating on tap and binding all the subsequent events like `touchend` and `pointerup`
  // it is being set while the first `onTap` and being unset during the next event loop cycle (via setTimeout(..., 0))
  private eventId: string | null = null
  private eventIdTimeout: ReturnType<typeof setTimeout> | null = null

  render () {
    const { children, childRef } = this.props

    const onlyChild = Children.only(children) as ReactElement

    const ref: Ref<HTMLElement> = (node) => {
      this.rootNode = node

      if (childRef && typeof childRef === 'function') {
        childRef(node)
      }
    }

    const clonedElementProps: DOMAttributes<any> & ClassAttributes<any> = { ref }
    SUPPORTED_MOUSE_HANDLERS.forEach((handlerName) => {
      const childHandler = onlyChild.props[handlerName]
      const propsHandler = this.props[handlerName]

      if (propsHandler) {
        const clonedElementHandler = (e: MouseEvent<HTMLElement>) => {
          childHandler && childHandler(e)

          propsHandler(e)
        }

        clonedElementProps[handlerName] = clonedElementHandler
      }
    })

    return cloneElement(onlyChild, clonedElementProps)
  }

  componentDidMount () {
    if (this.rootNode) {
      this.rootHammer = new Hammer(this.rootNode)
      this.rootHammer.on('tap', this.onTap)
      this.rootNode.addEventListener('touchend', this.onTouchEnd)
    }
  }

  componentWillUnmount () {
    if (this.eventIdTimeout) {
      clearTimeout(this.eventIdTimeout)
    }

    if (this.rootNode && this.rootHammer) {
      this.rootHammer.off('tap', this.onTap)
      this.rootHammer.stop(true)
      this.rootHammer.destroy()
      this.rootHammer = null

      this.rootNode.removeEventListener('touchend', this.onTouchEnd)
    }
  }

  private onTouchEnd = (e: TouchEvent) => {
    // mute `click`, 'mouseEnter`, `mouseLeave` events
    if (this.eventId && e.cancelable) {
      e.preventDefault()
    }
  }

  private onTap: HammerListener = (e) => {
    this.eventId = generate()

    this.eventIdTimeout = setTimeout(() => {
      this.eventId = null
    }, 0)

    if (e.pointerType === 'mouse') {
      return
    }

    if (this.props.onTap) {
      this.props.onTap(e)
    }
  }
}

export default Touchable
